/**
 * Depreciation runs — `/finance/depreciation-runs`.
 *
 * A run posts every scheduled charge for one fiscal period, summarized into
 * ONE journal per asset class. There is at most one run per period, and the
 * run is idempotent through two independent database facts, so a retry finds
 * nothing due and completes with zero new journals rather than double-charging
 * the month. That is why re-running an already-depreciated period is a normal
 * outcome (`assets.period_already_depreciated`) and not a scary one.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";

import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import { executeDepreciationRun, type DepreciationRun, type DepreciationRunCreate } from "../api";
import {
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  SelectField,
  StatusBadge,
} from "../components/fields";
import { financeKeys, useAssetClasses, useDepreciationRuns, useFiscalPeriods } from "../queries";

const NONE = "__none__";

const RUN_STATUS_VARIANTS = {
  pending: "warning",
  completed: "success",
  failed: "destructive",
} as const;

export interface DepreciationRunsPageProps {
  canExecute?: boolean;
  canReadFiscalPeriods?: boolean;
  canReadClasses?: boolean;
}

export function DepreciationRunsPage({
  canExecute = true,
  canReadFiscalPeriods = true,
  canReadClasses = true,
}: DepreciationRunsPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const runsQuery = useDepreciationRuns();
  const periodsQuery = useFiscalPeriods(canReadFiscalPeriods);
  const classesQuery = useAssetClasses(canReadClasses);

  const [fiscalPeriodId, setFiscalPeriodId] = useState(NONE);
  const [formError, setFormError] = useState<string | null>(null);

  const periodName = useMemo(() => {
    const byId = new Map((periodsQuery.data ?? []).map((period) => [period.id, period.name]));
    return (id: string): string => byId.get(id) ?? id;
  }, [periodsQuery.data]);

  const className = useMemo(() => {
    const byId = new Map(
      (classesQuery.data ?? []).map((assetClass) => [
        assetClass.id,
        `${assetClass.code} · ${assetClass.name}`,
      ]),
    );
    return (id: string): string => byId.get(id) ?? id;
  }, [classesQuery.data]);

  const executeMutation = useIdempotentMutation<DepreciationRun, DepreciationRunCreate>({
    mutationFn: (body, key) => executeDepreciationRun(body, key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.depreciationRuns.all });
      void queryClient.invalidateQueries({ queryKey: financeKeys.fixedAssets.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const submit = () => {
    if (fiscalPeriodId === NONE) {
      setFormError(t("finance.depreciationRuns.periodRequired", "Choose the fiscal period."));
      return;
    }
    setFormError(null);
    executeMutation.mutate({ fiscal_period_id: fiscalPeriodId });
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.depreciationRuns.title", "Depreciation runs")}
        description={t(
          "finance.depreciationRuns.subtitle",
          "One run per fiscal period, summarized into one journal per asset class.",
        )}
      />

      {canExecute ? (
        <Card>
          <CardHeader>
            <CardTitle>
              <h2>{t("finance.depreciationRuns.runSection", "Run a period")}</h2>
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <SelectField
              id="depreciation-period"
              label={t("finance.depreciationRuns.period", "Fiscal period")}
              value={fiscalPeriodId}
              onValueChange={setFiscalPeriodId}
              className="w-72"
              disabled={!canReadFiscalPeriods}
              options={[
                { value: NONE, label: t("finance.common.choose", "Choose…") },
                ...(periodsQuery.data ?? []).map((period) => ({
                  value: period.id,
                  label: `${period.name} (${period.starts_on} → ${period.ends_on})`,
                })),
              ]}
            />
            <Button type="button" loading={executeMutation.isPending} onClick={submit}>
              {t("finance.depreciationRuns.execute", "Run depreciation")}
            </Button>
            <p className="w-full text-sm text-muted-foreground">
              {t(
                "finance.depreciationRuns.runHint",
                "Re-running a period that already posted is safe: it finds nothing due and posts nothing.",
              )}
            </p>
            {formError === null ? null : (
              <div className="w-full">
                <ProblemBanner>{formError}</ProblemBanner>
              </div>
            )}
            {executeMutation.data === undefined ? null : (
              <div className="w-full">
                <NoticeBanner>
                  {t(
                    "finance.depreciationRuns.runResult",
                    "{{assets}} assets charged across {{journals}} journals.",
                    {
                      assets: executeMutation.data.asset_count,
                      journals: executeMutation.data.journal_count,
                    },
                  )}
                </NoticeBanner>
              </div>
            )}
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>
            <h2>{t("finance.depreciationRuns.historySection", "History")}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {(runsQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("finance.depreciationRuns.empty", "No depreciation has been run yet.")}
            </p>
          ) : (
            (runsQuery.data ?? []).map((run) => (
              <div key={run.id} className="flex flex-col gap-2 rounded-md border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{periodName(run.fiscal_period_id)}</span>
                  <StatusBadge
                    status={run.status}
                    label={t(`finance.depreciationRuns.statusOption.${run.status}`, run.status)}
                    variants={RUN_STATUS_VARIANTS}
                  />
                  <MoneyDisplay value={run.total_amount} />
                </div>
                <p className="text-sm text-muted-foreground">
                  {t(
                    "finance.depreciationRuns.runSummary",
                    "{{assets}} assets · {{journals}} journals",
                    { assets: run.asset_count, journals: run.journal_count },
                  )}
                </p>
                {run.postings.length === 0 ? null : (
                  <ul className="flex flex-col divide-y text-sm">
                    {run.postings.map((posting) => (
                      <li
                        key={posting.id}
                        className="flex flex-wrap items-center justify-between gap-2 py-1"
                      >
                        <span>{className(posting.class_id)}</span>
                        <span className="text-muted-foreground">
                          {t("finance.depreciationRuns.assetCount", "{{count}} assets", {
                            count: posting.asset_count,
                          })}
                        </span>
                        <MoneyDisplay value={posting.amount} />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
